import { InvestmentAccountCloseState } from "@/admin/types";
import { FetchState, DrivewealthAccountCancelUploadListPage } from "@/shared/types";
import { defineStore } from "pinia";
import { templatedFetchKeyCollectionStore } from "@/shared/store/fetchKeyedCollectionPlugin";
import * as drivewealthService from "@/shared/api/drivewealthService";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import Vue from "vue";
import { useNotifierStore } from "@/shared/store/notifier";
import { downloadCsvFile } from "@/shared/fileDownload";

const PageSize = 10;

const useUploadsSubStore = defineStore("investmentAccountClose_uploads", () => {
    return templatedFetchKeyCollectionStore<DrivewealthAccountCancelUploadListPage, DrivewealthAccountCancelUploadListPage>(
        drivewealthService.getAccountCloseFileRequests,
    );
});

export const initialState: InvestmentAccountCloseState = {
    investmentAccountCloseFileUploadState: FetchState.Uninitialized,
    investmentAccountCloseFetchState: FetchState.Uninitialized
};

export const useInvestmentAccountCloseStore = defineStore("investmentAccountClose", {
    state: (): InvestmentAccountCloseState => (_.cloneDeep(initialState)),
    getters: {
        isUploadingAccountCloseFile: state => state.investmentAccountCloseFileUploadState === FetchState.Loading,
        isLoaded: state => state.investmentAccountCloseFetchState === FetchState.Loaded,
        uploads: () => useUploadsSubStore(),
        isPageLoaded () {
            return pageNumber => {
                const uploadsListPage = this.uploads.fetchStates[`${pageNumber}`];

                if (uploadsListPage) {
                    return uploadsListPage === FetchState.Loaded;
                }

                return false;
            };
        },
        pageTotal () {
            return pageNumber => {
                const uploadsListPage = this.uploads.items[`${pageNumber}`];

                if (uploadsListPage) {
                    return uploadsListPage.numberOfPages;
                }

                return 1;
            };
        },
        pagedUploadsById () {
            return pageNumber => {
                const uploadsListPage = this.uploads.items[`${pageNumber}`];

                if (uploadsListPage) {
                    return uploadsListPage.records;
                }

                return [];
            };
        },
    },
    actions: {
        setAccountCloseFileUploadFetchState (fetchState: FetchState) {
            this.investmentAccountCloseFileUploadState = fetchState;
        },
        setAccountCloseFetchState (fetchState: FetchState) {
            this.investmentAccountCloseFetchState = fetchState;
        },
        setUploadsFetchState ({ fetchState, itemKey }:
            {
                fetchState: FetchState,
                itemKey: string,
                pageNumber: number
            }) {
            Vue.set(this.uploads.fetchStates, itemKey, fetchState);
        },
        uploadAccountCloseFile ({ file }) {
            this.setAccountCloseFileUploadFetchState (FetchState.Loading);
            return drivewealthService.uploadAccountCloseFile(file)
                .then(() => {
                    const notifierStore = useNotifierStore();
                    this.setAccountCloseFileUploadFetchState(FetchState.Loaded);
                    notifierStore.setSuccess("File was uploaded successfully");
                })
                .catch(err => {
                    this.setAccountCloseFileUploadFetchState (FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async getUploads (
            args: {
                page: number
            }
        ): Promise<any> {
            const itemKey = `${args.page}`;
            if (this.uploads.fetchStates[itemKey] === FetchState.Loading ||
                this.uploads.fetchStates[itemKey] === FetchState.Loaded) {
                return Promise.resolve();
            }

            return useUploadsSubStore().fetchKeyedApiCall(
                    { },
                    itemKey,
                    { page: args.page, pageSize: PageSize }
                )
                .catch(err => {
                    this.setUploadsFetchState({
                        itemKey, fetchState: FetchState.Uninitialized
                    });
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        downloadResultsFileCsv (params: { drivewealthAccountCancelUploadId: number }): Promise<any> {
            return drivewealthService.downloadResultsFileCsv(params.drivewealthAccountCancelUploadId)
                .then(response => {
                    const fileName = `close-account-results_${params.drivewealthAccountCancelUploadId}.csv`;
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});